import { useState } from "react";
import Image from "next/image";
import { Card, CardContent, CardHeader } from "@tracify/ui/card";
import { Button } from "@tracify/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tracify/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tracify/ui/select";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import {
  LinkSlashIcon,
  PencilSquareIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { useFeatureFlags } from "@lib/hooks/useFeatureFlags";
import {
  type OutbrainEventName,
  useCreateOutbrainEventName,
  useDeleteOutbrainEventName,
  useOutbrainEventNames,
  useUpdateOutbrainEventName,
} from "@lib/api-hooks/useAdAccounts/useOutbrainEventName";
import { useGetPostbackSourceStatus } from "@lib/api-hooks/useAdAccounts/useGetPostbackSourceStatus";
import { useSetPostbackSourceStatus } from "@lib/api-hooks/useAdAccounts/useSetPostbackSourceStatus";
import AdAccountPostbackControl from "./AdAccountPostbackControl";

const OUTBRAIN_EVENT_NAMES = [
  "Tracify_Purchase",
  "Tracify_NewCustomerPurchase",
  "Tracify_ReturningCustomerPuchase",
] as const;

type OutbrainEventNameOption = (typeof OUTBRAIN_EVENT_NAMES)[number];

const isOutbrainEventNameOption = (
  value: string
): value is OutbrainEventNameOption =>
  OUTBRAIN_EVENT_NAMES.some((eventName) => eventName === value);

export function OutbrainConversionPostbackCard() {
  const { getFlag } = useFeatureFlags();
  const conversionPostbackEnabled =
    getFlag("conversion_postback_enabled")?.enabled ?? false;
  const { selectedOrganisationData } = useSelectedStores();
  const organisationId = selectedOrganisationData?.id ?? "";

  const { data: eventNames = [], isLoading } = useOutbrainEventNames(
    { organisationId },
    { enabled: conversionPostbackEnabled && !!organisationId }
  );
  const { data: postbackStatus, isFetching: isFetchingPostbackStatus } =
    useGetPostbackSourceStatus(organisationId, {
      enabled: conversionPostbackEnabled && !!organisationId,
    });
  const createEventNameMutation = useCreateOutbrainEventName();
  const updateEventNameMutation = useUpdateOutbrainEventName();
  const deleteEventNameMutation = useDeleteOutbrainEventName();
  const setPostbackSourceStatusMutation = useSetPostbackSourceStatus();

  const [eventNameDialogOpen, setEventNameDialogOpen] = useState(false);
  const [editingEventName, setEditingEventName] =
    useState<OutbrainEventName | null>(null);
  const [eventNameInput, setEventNameInput] = useState<
    OutbrainEventNameOption | ""
  >("");
  const [errorMessage, setErrorMessage] = useState("");

  if (!conversionPostbackEnabled) return null;

  const isConnected = !isLoading && eventNames.length > 0;
  const isSourceActive =
    postbackStatus?.activeForPostback.includes("outbrain") ?? false;
  const isMutating =
    createEventNameMutation.isPending ||
    updateEventNameMutation.isPending ||
    deleteEventNameMutation.isPending ||
    setPostbackSourceStatusMutation.isPending;

  const openCreateEventNameDialog = () => {
    setEditingEventName(null);
    setEventNameInput("");
    setErrorMessage("");
    setEventNameDialogOpen(true);
  };

  const openUpdateEventNameDialog = (eventName: OutbrainEventName) => {
    setEditingEventName(eventName);
    setEventNameInput(
      isOutbrainEventNameOption(eventName.name) ? eventName.name : ""
    );
    setErrorMessage("");
    setEventNameDialogOpen(true);
  };

  const closeEventNameDialog = () => {
    setEditingEventName(null);
    setEventNameInput("");
    setErrorMessage("");
    setEventNameDialogOpen(false);
  };

  const handleEventNameDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      closeEventNameDialog();
    }
  };

  const handleSubmitEventName = async () => {
    if (!eventNameInput) {
      setErrorMessage("Please select an Outbrain event name");
      return;
    }

    if (!organisationId) {
      setErrorMessage("Organisation ID is required");
      return;
    }

    try {
      if (editingEventName) {
        await updateEventNameMutation.mutateAsync({
          id: editingEventName.id,
          name: eventNameInput,
        });
        toast.success("Outbrain event name updated successfully");
      } else {
        await createEventNameMutation.mutateAsync({
          organisationId,
          name: eventNameInput,
        });
        if (eventNames.length === 0) {
          await setPostbackSourceStatusMutation.mutateAsync({
            source: "outbrain",
            organisationId,
            isActiveForPostback: true,
          });
        }
        toast.success("Outbrain event name connected successfully");
      }
      closeEventNameDialog();
    } catch (error) {
      toast.error(
        (error as Error)?.message ?? "Failed to save Outbrain event name"
      );
    }
  };

  const handleDeleteEventName = async (eventName: OutbrainEventName) => {
    try {
      await deleteEventNameMutation.mutateAsync({ id: eventName.id });
      if (eventNames.length <= 1 && organisationId) {
        await setPostbackSourceStatusMutation.mutateAsync({
          source: "outbrain",
          organisationId,
          isActiveForPostback: false,
        });
      }
      toast.success("Outbrain event name disconnected successfully");
    } catch (error) {
      toast.error(
        (error as Error)?.message ?? "Failed to disconnect Outbrain event name"
      );
    }
  };

  const handleSourceToggle = async (checked: boolean) => {
    if (!organisationId) return;

    try {
      await setPostbackSourceStatusMutation.mutateAsync({
        source: "outbrain",
        organisationId,
        isActiveForPostback: checked,
      });
      toast.success(
        checked
          ? "Conversion postback enabled successfully"
          : "Conversion postback disabled successfully"
      );
    } catch (error) {
      toast.error(
        (error as Error)?.message ??
          `Failed to ${checked ? "enable" : "disable"} conversion postback`
      );
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-muted w-12 h-12 flex items-center justify-center rounded-lg shrink-0">
                <Image
                  src="/static/logos/outbrain-logo.svg"
                  alt="Outbrain"
                  width={28}
                  height={28}
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </div>
              <p className="h4 text-foreground">Conversion Postback</p>
            </div>
            <div className="flex items-center gap-3">
              {!isLoading &&
                (isConnected ? (
                  <div className="flex items-center gap-1.5">
                    {isSourceActive ? (
                      <CheckCircleIcon className="text-green-500 w-5 h-5" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-foreground border border-dashed" />
                    )}
                    <span className="text-sm">
                      {isSourceActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <div className="h-5 w-5 rounded-full border-foreground border border-dashed" />
                    <span className="text-sm">Not connected</span>
                  </div>
                ))}
              {!isLoading ? (
                <Button
                  variant="primary"
                  className="bg-[#5d5fef] hover:bg-[#5d5fef]/90 text-white"
                  disabled={isMutating || !organisationId}
                  onClick={openCreateEventNameDialog}
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create event name
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : eventNames.length > 0 ? (
            <>
              <AdAccountPostbackControl
                checked={isSourceActive}
                disabled={isFetchingPostbackStatus || isMutating}
                onCheckedChange={(checked) => {
                  void handleSourceToggle(checked);
                }}
                helperText={
                  isSourceActive
                    ? "Conversion postback is enabled for connected Outbrain event names."
                    : "When enabled Tracify will send conversion postback events using the connected Outbrain event names."
                }
              />
              <div className="space-y-4">
                {eventNames.map((eventName) => (
                  <div
                    key={eventName.id}
                    className="rounded-lg px-5 py-4 !bg-background"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 space-y-1">
                        <p className="text-sm text-muted-foreground">
                          Event name
                        </p>
                        <p className="truncate font-medium">{eventName.name}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Update Outbrain event name ${eventName.name}`}
                          disabled={isMutating}
                          onClick={() => openUpdateEventNameDialog(eventName)}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Delete Outbrain event name ${eventName.name}`}
                          disabled={isMutating}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            void handleDeleteEventName(eventName);
                          }}
                        >
                          <LinkSlashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              No Outbrain event name connected. Use the button above to enable
              conversion postback.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={eventNameDialogOpen}
        onOpenChange={handleEventNameDialogOpenChange}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEventName
                ? "Update Outbrain event name"
                : "Connect Outbrain event name"}
            </DialogTitle>
            <DialogDescription>
              Create the corresponding conversion event in Outbrain with exactly
              the same name before connecting it here. Tracify sends postbacks
              only to the three supported event names below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="outbrain_event_name"
                className="block text-sm font-medium mb-1"
              >
                Outbrain event name
              </label>
              <Select
                value={eventNameInput || undefined}
                onValueChange={(value) => {
                  if (!isOutbrainEventNameOption(value)) return;
                  setEventNameInput(value);
                  if (errorMessage) {
                    setErrorMessage("");
                  }
                }}
              >
                <SelectTrigger
                  id="outbrain_event_name"
                  aria-invalid={Boolean(errorMessage)}
                >
                  <SelectValue placeholder="Select an Outbrain event name" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {OUTBRAIN_EVENT_NAMES.map((eventName) => (
                      <SelectItem key={eventName} value={eventName}>
                        {eventName}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            {errorMessage ? (
              <p className="font-medium text-sm text-red-500">{errorMessage}</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              disabled={isMutating}
              onClick={closeEventNameDialog}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={
                createEventNameMutation.isPending ||
                updateEventNameMutation.isPending
              }
              disabled={
                createEventNameMutation.isPending ||
                updateEventNameMutation.isPending ||
                !eventNameInput
              }
              onClick={() => {
                void handleSubmitEventName();
              }}
            >
              {editingEventName ? "Update event name" : "Connect event name"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
